import { useEffect, useState, useMemo, CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  EyeIcon,
  UsersIcon,
  ChartBarIcon,
  ClockIcon,
  CalendarIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';
import { dashboardService, DashboardStats, DashboardActivity } from '../services/dashboard.service';
import { VirtualList } from '@/components/common/VirtualList';

// Animation variants - defined outside component to avoid recreation
const CONTAINER_VARIANTS = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05, // Reduced from 0.1 for faster animation
    },
  },
};

const ITEM_VARIANTS = {
  hidden: { opacity: 0, y: 10 }, // Reduced y from 20 for subtler animation
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.2, ease: 'easeOut' }, // Reduced from 0.4
  },
};

// Static color classes - defined outside component to avoid recreation
const SEVERITY_COLORS = {
  low: 'bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200',
  medium: 'bg-warning-100 text-warning-800 dark:bg-warning-900 dark:text-warning-200',
  high: 'bg-danger-100 text-danger-800 dark:bg-danger-900 dark:text-danger-200',
  critical: 'bg-danger-200 text-danger-900 dark:bg-danger-800 dark:text-danger-100',
} as const;

// Item height for virtualization (includes padding)
const ACTIVITY_ITEM_HEIGHT = 120;

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ComponentType<{ className?: string }>;
  color?: 'primary' | 'danger' | 'warning' | 'success';
}

function StatCard({ title, value, icon: Icon, color = 'primary' }: StatCardProps) {
  const colorClasses = {
    primary: 'bg-primary-500 text-white',
    danger: 'bg-danger-500 text-white',
    warning: 'bg-warning-500 text-white',
    success: 'bg-success-500 text-white',
  };

  return (
    <Card hover="default" className="transition-all duration-200 h-full">
      <CardContent className="p-5 sm:p-6 h-full">
        {/* The title gets its own full-width row so a long label like
            "Active Investigations" wraps cleanly at every breakpoint. The value
            and icon share the row below: both are small (a number + a fixed,
            flex-shrink-0 icon), so they can never collide or overflow the card. */}
        <p className="text-sm font-medium leading-tight text-secondary-600 dark:text-secondary-400">
          {title}
        </p>
        <div className="flex items-center justify-between gap-3 mt-3">
          <p className="text-3xl font-bold text-secondary-900 dark:text-white">
            {value}
          </p>
          <div className={cn('p-3 rounded-lg flex-shrink-0', colorClasses[color])}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

interface RecentActivityItem {
  id: string;
  type: 'incident' | 'threat' | 'activity' | 'user' | 'security';
  title: string;
  description: string;
  timestamp: string;
  severity?: 'low' | 'medium' | 'high' | 'critical';
  user?: string;
}

function RecentActivityList({ activities }: { activities: RecentActivityItem[] }) {
  if (activities.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4">
        <div className="w-16 h-16 rounded-full bg-secondary-100 dark:bg-secondary-800 flex items-center justify-center mb-4">
          <CalendarIcon className="w-8 h-8 text-secondary-400 dark:text-secondary-600" />
        </div>
        <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
          No activities today
        </h3>
        <p className="text-sm text-secondary-600 dark:text-secondary-400 text-center max-w-sm">
          No activities or incidents have been recorded today. Check back later or create a new incident or activity.
        </p>
      </div>
    );
  }

  return (
    <VirtualList
      items={activities}
      itemHeight={ACTIVITY_ITEM_HEIGHT}
      maxHeight={500}
      overscanCount={3}
      renderItem={(activity, _index, style) => {
        const containerStyle: CSSProperties = {
          ...style,
          paddingBottom: '12px',
        };

        return (
          <div key={activity.id} style={containerStyle}>
            <div
              className="flex items-start space-x-4 p-4 rounded-lg border border-secondary-200 dark:border-secondary-700 hover:bg-secondary-50 dark:hover:bg-secondary-800/50 transition-colors h-full"
            >
              <div className="w-2 h-2 rounded-full bg-primary-500 mt-2 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-secondary-900 dark:text-white truncate">
                    {activity.title}
                  </h4>
                  {activity.severity && (
                    <span
                      className={cn(
                        'px-2 py-1 text-xs font-medium rounded-full',
                        SEVERITY_COLORS[activity.severity]
                      )}
                    >
                      {activity.severity.toUpperCase()}
                    </span>
                  )}
                </div>
                <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1 line-clamp-2">
                  {activity.description}
                </p>
                <p className="text-xs text-secondary-500 dark:text-secondary-500 mt-2 flex items-center">
                  <ClockIcon className="w-3 h-3 mr-1" />
                  {activity.timestamp}
                </p>
              </div>
            </div>
          </div>
        );
      }}
    />
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [activities, setActivities] = useState<DashboardActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const [statsData, activitiesData] = await Promise.all([
          dashboardService.getStats(),
          dashboardService.getRecentActivity()
        ]);
        setStats(statsData);
        setActivities(activitiesData);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch dashboard data:', err);
        setError('Failed to load dashboard data');
        // Fallback to mock data if API fails
        setStats({
          activeIncidents: { total: 0, change: 'No data', changeType: 'neutral' },
          upcomingMissions: { total: 0, change: 'No data', changeType: 'neutral' },
          activeThreats: { total: 0, change: 'No data', changeType: 'neutral' },
          activeInvestigations: { total: 0, change: 'No data', changeType: 'neutral' },
          system: { totalRoles: 0, recentLogins: 0, uptime: '0%', lastUpdated: new Date().toISOString() }
        });
        setActivities([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Memoize the activity mapping - must be called before any early returns (React hooks rules)
  const recentActivities: RecentActivityItem[] = useMemo(() =>
    activities.map(activity => ({
      id: activity.id,
      type: activity.type,
      title: activity.title,
      description: activity.description,
      timestamp: activity.timestamp,
      severity: activity.severity?.toLowerCase() as 'low' | 'medium' | 'high' | 'critical' | undefined,
      user: activity.user
    })), [activities]);

  if (loading) {
    return (
      <div className="p-8">
        <div className="text-center">Loading dashboard data...</div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="p-8">
        <div className="text-center text-red-600">
          {error ?? 'Failed to load dashboard data'}
        </div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Active Incidents',
      value: stats.activeIncidents.total,
      icon: ExclamationTriangleIcon,
      color: 'danger' as const,
    },
    {
      title: 'Upcoming Activities',
      value: stats.upcomingMissions.total,
      icon: CalendarIcon,
      color: 'primary' as const,
    },
    {
      title: 'Active Threats',
      value: stats.activeThreats.total,
      icon: EyeIcon,
      color: 'warning' as const,
    },
    {
      title: 'Active Investigations',
      value: stats.activeInvestigations.total,
      icon: MagnifyingGlassIcon,
      color: 'success' as const,
    },
  ];

  return (
    <motion.div
      variants={CONTAINER_VARIANTS}
      initial="hidden"
      animate="visible"
      className="p-4 sm:p-6 space-y-4 sm:space-y-6"
    >
      {/* Header */}
      <motion.div variants={ITEM_VARIANTS}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">
              Security Dashboard
            </h1>
            <p className="text-secondary-600 dark:text-secondary-400 mt-1">
              Real-time overview of your security operations
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <Link to="/security/analytics">
              <Button variant="outline" className="w-full sm:w-auto">
                <ChartBarIcon className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">View Reports</span>
                <span className="sm:hidden">Reports</span>
              </Button>
            </Link>
            <Link to="/security/incidents/create">
              <Button className="w-full sm:w-auto">
                <ExclamationTriangleIcon className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">New Incident</span>
                <span className="sm:hidden">Incident</span>
              </Button>
            </Link>
          </div>
        </div>
      </motion.div>

      {/* Stats Grid */}
      {/* 4-up only at xl: with the 256px sidebar expanded, four columns at lg
          (~1024px) leave each card too narrow for its number + icon, so we stay
          2-up through the 1024–1279 range and only go 4-up when there's room. */}
      <motion.div variants={ITEM_VARIANTS} className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6">
        {statCards.map((stat) => (
          <motion.div
            key={stat.title}
            variants={ITEM_VARIANTS}
          >
            <StatCard {...stat} />
          </motion.div>
        ))}
      </motion.div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Recent Activity */}
        <motion.div variants={ITEM_VARIANTS}>
          <Card>
            <CardHeader>
              <CardTitle>Upcoming Incidents, Activities & Investigations</CardTitle>
              <CardDescription>
                Open and active items across your organization
              </CardDescription>
            </CardHeader>
            <CardContent>
              <RecentActivityList activities={recentActivities} />
            </CardContent>
          </Card>
        </motion.div>

        {/* Quick Actions */}
        <motion.div variants={ITEM_VARIANTS}>
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
              <CardDescription>
                Frequently used actions for security operations
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <Link to="/security/incidents/create">
                  <Button className="w-full justify-start" variant="outline">
                    <ExclamationTriangleIcon className="w-4 h-4 mr-3" />
                    Report New Incident
                  </Button>
                </Link>
                <div>
                  <Link to="/security/executive-protection">
                    <Button className="w-full justify-start" variant="outline">
                      <ShieldCheckIcon className="w-4 h-4 mr-3" />
                      Create Protection Activity
                    </Button>
                  </Link>
                </div>
                <div>
                  <Button className="w-full justify-start" variant="outline">
                    <EyeIcon className="w-4 h-4 mr-3" />
                    Add Threat Intelligence
                  </Button>
                </div>
                <div>
                  <Link to="/settings">
                    <Button className="w-full justify-start" variant="outline">
                      <UsersIcon className="w-4 h-4 mr-3" />
                      Manage Team Access
                    </Button>
                  </Link>
                </div>
                <div>
                  <Link to="/security/analytics">
                    <Button className="w-full justify-start" variant="outline">
                      <ChartBarIcon className="w-4 h-4 mr-3" />
                      Generate Security Report
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

    </motion.div>
  );
}